import React, { useEffect, useState, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useGoalStore } from '../../../stores/goalStore';
import { useDialog } from '../../../hooks/useDialog';
import { useWorkspaceStore } from '../../../stores/workspaceStore';
import { useAuthStore } from '../../../stores/authStore';
import { useTaskStore } from '../../../stores/taskStore';
import type { Goal } from '../../../types';

interface GoalSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedGoalId: string | null;
  onGoalSelect: (goalId: string | null) => void;
  onGoalDetail: (goalId: string | 'new', parentGoalId?: string) => void;
  onPausedGoalClick?: (goalId: string, targetElement?: HTMLElement) => void;
  onClosePausedPanel?: () => void;
}

export const GoalSidebar: React.FC<GoalSidebarProps> = ({
  isOpen,
  onClose,
  selectedGoalId,
  onGoalSelect,
  onGoalDetail,
  onPausedGoalClick,
  onClosePausedPanel
}) => {
  const { user } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const { updateTask, loadTasks, tasks } = useTaskStore();
  const {
    goals,
    loading,
    error,
    loadGoals,
    updateGoal,
    deleteGoal,
    getRootGoals,
    getSubGoals,
    clearError
  } = useGoalStore();
  const { confirm } = useDialog();

  const [hoveredGoalId, setHoveredGoalId] = useState<string | null>(null);
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const statusButtonRefs = useRef<{ [goalId: string]: HTMLButtonElement | null }>({});

  // 加载目标数据
  useEffect(() => {
    if (isOpen) {
      loadGoals(currentWorkspace.type, currentWorkspace.teamId);
    }
  }, [isOpen, loadGoals, currentWorkspace.type, currentWorkspace.teamId]);

  // 处理键盘按键和全局点击
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenStatusDropdown(null);
        setDropdownPosition(null);
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (openStatusDropdown) {
        setOpenStatusDropdown(null);
        setDropdownPosition(null);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose, openStatusDropdown]);

  // 获取激活的目标（只有ACTIVE状态的目标）
  const getActiveGoals = () => {
    return goals.filter(goal => goal.status === 'ACTIVE');
  };

  // 计算目标关联的任务数量（基于taskStore的实时数据）
  const getTaskCount = (goal: Goal): number => {
    const directTasks = tasks.filter(task => task.goalId === goal.id).length;
    const subGoals = getSubGoals(goal.id);
    const subTasksCount = subGoals.reduce((count, subGoal) =>
      count + tasks.filter(task => task.goalId === subGoal.id).length, 0
    );
    return directTasks + subTasksCount;
  };

  // 获取所有激活目标的任务总数（基于taskStore的实时数据）
  const getActiveTotalTaskCount = (): number => {
    const activeGoals = getActiveGoals();
    const activeGoalIds = activeGoals.map(goal => goal.id);
    return tasks.filter(task => task.goalId && activeGoalIds.includes(task.goalId)).length;
  };

  // 获取目标可用的状态选项
  const getAvailableStatusOptions = (currentStatus: GoalStatus) => {
    const options = [];

    switch (currentStatus) {
      case 'ACTIVE':
        options.push(
          {
            status: 'PAUSED',
            label: '暂停',
            icon: (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            ),
            color: 'text-yellow-600'
          },
          {
            status: 'COMPLETED',
            label: '标记完成',
            icon: (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ),
            color: 'text-green-600'
          }
        );
        break;
      case 'PAUSED':
        options.push(
          {
            status: 'ACTIVE',
            label: '继续进行',
            icon: (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            ),
            color: 'text-blue-600'
          },
          {
            status: 'COMPLETED',
            label: '标记完成',
            icon: (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ),
            color: 'text-green-600'
          }
        );
        break;
      case 'COMPLETED':
      case 'CANCELLED':
        // 终态，无法切换
        break;
    }

    return options;
  };

  // 切换目标状态
  const changeGoalStatus = async (goal: Goal, newStatus: GoalStatus, e: React.MouseEvent) => {
    e.stopPropagation();

    // 在异步操作前保存目标元素引用
    const goalElement = (e.currentTarget?.closest('[data-goal-id]') || e.currentTarget) as HTMLElement;

    // 状态切换确认
    let confirmOptions = null;
    switch (newStatus) {
      case 'COMPLETED':
        confirmOptions = {
          title: '完成目标',
          message: `确定要完成目标"${goal.title}"吗？完成后将无法修改状态，并且所有关联的任务都会被标记为完成。`,
          confirmText: '完成',
          cancelText: '取消',
          confirmVariant: 'primary' as const
        };
        break;
    }

    if (confirmOptions) {
      const confirmed = await confirm(confirmOptions);
      if (!confirmed) {
        return;
      }
    }

    try {
      await updateGoal(goal.id, { status: newStatus });

      // 如果目标被标记为完成，自动完成所有关联的任务
      if (newStatus === 'COMPLETED') {
        const goalTasks = tasks.filter(task => task.goalId === goal.id);
        for (const task of goalTasks) {
          if (task.status !== 'DONE') {
            await updateTask(task.id, { status: 'DONE' });
          }
        }
      }

      // 如果目标从暂停状态变为激活状态，关闭暂停面板
      if (goal.status === 'PAUSED' && newStatus === 'ACTIVE' && onClosePausedPanel) {
        onClosePausedPanel();
      }

      // 如果目标从激活状态变为暂停状态，自动打开暂停面板
      if (goal.status === 'ACTIVE' && newStatus === 'PAUSED' && onPausedGoalClick) {
        onPausedGoalClick(goal.id, goalElement);
      }

      // 重新加载任务数据以确保过滤状态同步
      await loadTasks();
    } catch (error) {
      // 错误已在store中处理
    }
  };

  // 删除目标
  const handleDeleteGoal = async (goal: Goal, e: React.MouseEvent) => {
    e.stopPropagation();

    const taskCount = getTaskCount(goal);
    const subGoals = getSubGoals(goal.id);

    let message = `确定要删除目标"${goal.title}"吗？`;
    if (taskCount > 0) {
      message += `\n该目标关联了 ${taskCount} 个任务，删除后这些任务将变为独立任务。`;
    }
    if (subGoals.length > 0) {
      message += `\n该目标包含 ${subGoals.length} 个子目标，它们将被一起删除。`;
    }

    const confirmed = await confirm({
      title: '删除目标',
      message,
      type: 'danger'
    });

    if (confirmed) {
      try {
        await deleteGoal(goal.id);
        // 重新加载任务数据以确保状态同步
        await loadTasks();
      } catch (error) {
        // 错误已在store中处理
      }
    }
  };

  // GoalDropZone 组件
  const GoalDropZone: React.FC<{ goal: Goal; children: React.ReactNode }> = ({ goal, children }) => {
    const { isOver, setNodeRef } = useDroppable({
      id: `goal-${goal.id}`,
      data: {
        type: 'goal',
        goalId: goal.id
      }
    });

    return (
      <div
        ref={setNodeRef}
        className={`transition-all duration-200 ${
          isOver ? 'bg-blue-100 border-blue-300' : ''
        }`}
      >
        {children}
      </div>
    );
  };

  // 渲染目标项
  const renderGoalItem = (goal: Goal, level: number = 0) => {
    const isSelected = selectedGoalId === goal.id;
    const isHovered = hoveredGoalId === goal.id;
    const isActive = goal.status === 'ACTIVE';
    const taskCount = getTaskCount(goal);
    const subGoals = getSubGoals(goal.id);
    const isOwner = goal.ownerId === user?.id;

    return (
      <div key={goal.id}>
        <GoalDropZone goal={goal}>
          <div
            data-goal-id={goal.id}
            className={`group relative px-3 py-3.5 cursor-pointer transition-all rounded-lg overflow-hidden ${
              isSelected
                ? 'bg-neutral-50 border border-neutral-200 shadow-sm'
                : 'hover:bg-neutral-50'
            }`}
            style={{ paddingLeft: `${level * 16 + 12}px` }}
            onMouseEnter={() => setHoveredGoalId(goal.id)}
            onMouseLeave={() => setHoveredGoalId(null)}
            onClick={(e) => {
              const targetElement = e.currentTarget as HTMLElement; // 立即保存引用
              if (goal.status === 'PAUSED' && onPausedGoalClick) {
                onGoalSelect(goal.id); // 先设置选中状态
                onPausedGoalClick(goal.id, targetElement);
              } else {
                // 切换到其他目标时，如果有暂停目标面板打开，应该关闭它
                onGoalSelect(goal.id);
              }
            }}
          >
            {/* 删除按钮 - 悬浮在右上角 */}
            {isOwner && (
              <button
                onClick={(e) => handleDeleteGoal(goal, e)}
                className="absolute top-1 right-1 p-1.5 text-gray-400 hover:text-red-600 bg-white hover:bg-red-50 rounded-full shadow-sm transition-all opacity-0 group-hover:opacity-100 z-10"
                title="删除目标"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}

            {/* 主要内容区域 - 现在可以占满宽度 */}
            <div className="flex flex-col">
              <div className="flex items-start gap-3">
                {/* 状态图标 */}
                <div className="mt-1 flex-shrink-0">
                  {getGoalStatusIcon(goal.status)}
                </div>

                {/* 目标信息 - 现在占满剩余空间 */}
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-medium text-gray-800 leading-tight"
                    title={goal.description ? `${goal.title}\n\n${goal.description}` : goal.title}
                  >
                    {goal.title}
                  </div>
                  {goal.description && (
                    <div className="text-xs text-gray-500 mt-1 leading-tight" style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden'
                    }}>
                      {goal.description}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <span className="font-medium">{taskCount}</span>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>

                    {/* 操作按钮组 - 在任务数目同一行右侧 */}
                    {isOwner && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* 编辑按钮 */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onGoalDetail(goal.id);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="编辑目标"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>

                        {/* 状态切换下拉菜单或已完成提示 */}
                        {goal.status === 'COMPLETED' || goal.status === 'CANCELLED' ? (
                          // 终态状态：只显示图标和提示
                          <div
                            className={`p-1 rounded flex items-center gap-1 ${
                              goal.status === 'COMPLETED'
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                            title={goal.status === 'COMPLETED' ? '已完成，不可修改' : '已取消，不可修改'}
                          >
                            <div className="w-3 h-3">
                              {getGoalStatusIcon(goal.status)}
                            </div>
                          </div>
                        ) : (
                          // 可变状态：显示下拉菜单
                          <div className="relative">
                            <button
                              ref={(el) => statusButtonRefs.current[goal.id] = el}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openStatusDropdown === goal.id) {
                                  setOpenStatusDropdown(null);
                                  setDropdownPosition(null);
                                } else {
                                  const buttonElement = statusButtonRefs.current[goal.id];
                                  if (buttonElement) {
                                    const rect = buttonElement.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + 4,
                                      left: rect.right - 144 // 144px = w-36
                                    });
                                  }
                                  setOpenStatusDropdown(goal.id);
                                }
                              }}
                              className={`p-1 rounded transition-colors flex items-center gap-1 ${
                                isActive
                                  ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                                  : goal.status === 'PAUSED'
                                  ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
                                  : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                              }`}
                              title="切换状态"
                            >
                              <div className="w-3 h-3">
                                {getGoalStatusIcon(goal.status)}
                              </div>
                              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>

                            {/* 状态下拉菜单 */}
                            {openStatusDropdown === goal.id && dropdownPosition && (
                              <div
                                className="fixed w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                style={{
                                  top: dropdownPosition.top,
                                  left: dropdownPosition.left
                                }}>
                                {getAvailableStatusOptions(goal.status).map((option) => (
                                  <button
                                    key={option.status}
                                    onClick={(e) => {
                                      changeGoalStatus(goal, option.status as GoalStatus, e);
                                      setOpenStatusDropdown(null);
                                      setDropdownPosition(null);
                                    }}
                                    className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm ${option.color} first:rounded-t-lg last:rounded-b-lg`}
                                  >
                                    <span>{option.icon}</span>
                                    <span>{option.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* 添加子目标按钮 */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onGoalDetail('new', goal.id);
                          }}
                          className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="添加子目标"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 百分比 - 右对齐 */}
              {goal.progress !== undefined && (
                <div className="w-full flex justify-end mt-2">
                  <span className="text-xs font-medium text-gray-600">
                    {goal.progress}%
                  </span>
                </div>
              )}

              {/* 进度条 - 跨越整个节点宽度 */}
              {goal.progress !== undefined && (
                <div className="w-full h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300 bg-gradient-to-r from-emerald-200 to-emerald-300"
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </GoalDropZone>

        {/* 分隔线 - 只在根目标之间显示 */}
        {level === 0 && (
          <div className="border-b border-gray-100 mx-3"></div>
        )}

        {/* 渲染子目标 */}
        {subGoals.map(subGoal => renderGoalItem(subGoal, level + 1))}
      </div>
    );
  };

  // 获取目标状态图标
  const getGoalStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" title="进行中" />
        );
      case 'COMPLETED':
        return (
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" title="已完成">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'PAUSED':
        return (
          <div className="w-3 h-3 bg-yellow-500 rounded-full" title="暂停" />
        );
      case 'CANCELLED':
        return (
          <div className="w-3 h-3 bg-red-500 rounded-full" title="已取消" />
        );
      default:
        return (
          <div className="w-3 h-3 bg-gray-400 rounded-full" title="未知状态" />
        );
    }
  };

  const rootGoals = getRootGoals();

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 top-16 bottom-0 w-80 bg-neutral-25 border-r border-neutral-100 shadow-sm z-40 flex flex-col rounded-t-xl">
      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-100 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-xl">
        <h3 className="text-lg font-semibold text-gray-800">目标</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onGoalDetail('new')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-neutral-100 rounded-full transition-colors"
            disabled={loading.create}
            title="新建目标"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-neutral-100 rounded-full transition-colors"
            title="关闭"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="p-3 bg-red-50 border-b border-red-200 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700 font-semibold"
          >
            ×
          </button>
        </div>
      )}

      {/* 内容区域 */}
      <div className="flex-1 overflow-y-auto overflow-x-visible custom-scrollbar">
        {loading.goals ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-2">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600">加载目标中...</span>
          </div>
        ) : (
          <div className="p-2 pb-20">
            {/* 全部激活目标选项 */}
            <div
              className={`px-3 py-3.5 cursor-pointer transition-all rounded-lg border ${
                selectedGoalId === null
                  ? 'bg-neutral-50 border-neutral-200 shadow-sm'
                  : 'border-transparent hover:bg-neutral-50 hover:border-neutral-100'
              } mb-2`}
              onClick={() => onGoalSelect(null)}
            >
              <div className="flex items-start gap-3">
                <div className="mt-1 flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800">全部</div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                    <span className="font-medium">{getActiveTotalTaskCount()}</span>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* 目标列表 */}
            {rootGoals.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">还没有目标</p>
                <button
                  onClick={() => onGoalDetail('new')}
                  className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                >
                  创建第一个目标
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                {rootGoals.map(goal => renderGoalItem(goal))}
              </div>
            )}

            {/* 未关联目标的任务选项 */}
            <GoalDropZone goal={{ id: 'unlinked' } as Goal}>
              <div
                className={`px-3 py-3.5 cursor-pointer transition-all rounded-lg border ${
                  selectedGoalId === 'unlinked'
                    ? 'bg-neutral-50 border-neutral-200 shadow-sm'
                    : 'border-transparent hover:bg-neutral-50 hover:border-neutral-100'
                } mt-4`}
                onClick={() => onGoalSelect('unlinked')}
              >
              <div className="flex items-start gap-3">
                <div className="mt-1 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2v8h12V6H4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800">独立任务</div>
                  <div className="text-xs text-gray-500 mt-1">
                    未关联目标的任务
                  </div>
                </div>
              </div>
              </div>
            </GoalDropZone>
          </div>
        )}
      </div>
    </div>
  );
};